import React, { useEffect, useState } from 'react';
import Button from '../../components/Button/Button';
import { AiOutlineLoading } from "react-icons/ai";
import useFormValidation from '../../hooks/useFormValidation';
import { healthDeclarationValidationSchema, validate } from '../../utils/formValidation';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { errorToast } from '../../utils/errorHandling';
import { getHealthDeclarationById, postHealthDeclarationById, updateHealthDeclarationById } from '../../services/healthDeclaration';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";

const CreateHealthDeclaration = () => {
    dayjs.extend(utc);
    const { t } = useTranslation();
    const [originalData, setOriginalData] = useState("");
    const [formData, setFormData] = useState("");
    const [isNew, setIsNew] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { errors, validateField, validateAllFields } = useFormValidation(healthDeclarationValidationSchema(t));
    const [courseData, setCourseData] = useState(
        localStorage.getItem("courseData")
            ? JSON.parse(localStorage.getItem("courseData"))
            : null
    );
    const languageSelected = localStorage.getItem('i18nextLng');

    const fetchHealthDeclaration = async () => {
        setIsLoading(true);
        try {
            const data = await getHealthDeclarationById();
            if (data) {
                setFormData(data);
                setOriginalData(data);
                setIsNew(false);
            }
        } catch (error) {
            errorToast(error, t("personal_info.ActionFailed"), t);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHealthDeclaration();
    }, []);

    const handleInputChange = (e) => {
        const { id, value, name } = e.target;
        if (id === 'is_infectious') {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [name]: id,
            }));
            validateField(name, id);
        }
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };
    const renderOpenDate = () => {
        const openDate = dayjs(courseData.start_datetime).subtract(4, 'day');
        const today = dayjs();
        // Returns true if today is the same day or after the target date
        // (This ensures that once the day arrives, it stays true regardless of the hour)
        return today.isSame(openDate, 'day') || today.isAfter(openDate, 'day');
    }
    const handleSubmit = () => {
        const submitHealthDeclaration = async () => {
            try {
                const data = isNew ? await postHealthDeclarationById(formData) : await updateHealthDeclarationById(formData);
                if (data) {
                    fetchHealthDeclaration();
                    setIsEditing(false);
                    toast.success(t("health_declaration.UpdateSuccess"));
                }
            } catch (error) {
                errorToast(error, t("health_declaration.UpdateFailed"), t);
            } finally {
                setIsSubmitting(false);
            }
        }

        setIsSubmitting(true);

        let formValidationSchema = Object.keys(formData).filter(e => e !== "is_infectious");
        console.log(formValidationSchema);

        if (validateAllFields(formData, formValidationSchema)) {
            submitHealthDeclaration()
        } else {
            toast.error(t("general.ValidationFail"));
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <div className='info-container'>
                <h3 className='mb-2'>{t("health_declaration.title")}</h3>
                <p>
                    {languageSelected === "en"
                        ? courseData.course_ename
                        : courseData.course_cname}
                </p>
                {isLoading &&
                    <>
                        <AiOutlineLoading className='spin' />
                        <p>{t("health_declaration.msg_loading")}</p>
                    </>
                }
            </div>
            {!isLoading &&
                <>
                    <div className='action-buttons'>
                        {!isEditing ?
                            <div className='mt-3'>
                                {isNew &&
                                    <>
                                        <p className='error text-center m-0'>
                                            {t("health_declaration.OpenDate", {
                                                start_date: languageSelected === "en" ?
                                                    dayjs.utc(courseData.start_datetime).subtract(4, 'day').format("D MMMM")
                                                    :
                                                    dayjs.utc(courseData.start_datetime)
                                                        .subtract(4, 'day')
                                                        .locale("zh-cn")
                                                        .format("M月D日"),
                                            })}
                                        </p>
                                        {renderOpenDate() &&
                                            <p className='error text-center'>{t("health_declaration.CompleteForm")}</p>
                                        }
                                    </>

                                }
                                {renderOpenDate() &&
                                    <div className='d-flex justify-content-center'>
                                        <Button
                                            buttonStyle='btn--primary'
                                            buttonSize='btn--small'
                                            onClick={() => setIsEditing(true)}
                                        >{t("general.BTNedit")}</Button>
                                    </div>
                                }
                            </div>
                            :
                            <div className='mt-3'>
                                <div className='d-flex justify-content-center'>
                                    <Button buttonStyle='btn--primary' onClick={handleCancel} type="button">{t("general.BTNcancel")}</Button>
                                </div>
                                <p className='error mt-2 mb-0 text-center'>{t("health_declaration.StartComplete")}</p>
                            </div>
                        }
                    </div>
                    {
                        renderOpenDate() &&
                        <div className='form-wrapper mt-2'>
                            <h6 className='mt-3'>
                                <strong>
                                    {t('health_declaration.HealthDescription')}
                                    <span style={{ "color": "#ff0000" }}>{dayjs.utc(courseData.start_datetime).subtract(3, 'day').format("YYYY-MM-DD")}</span>
                                    {t('health_declaration.HealthDescription2')}
                                </strong>
                            </h6>
                            <form>
                                <div className="form-item">
                                    <label>{t("health_declaration.is_covid")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="is_covid" id="1" checked={formData.is_covid === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.yes")}</p>
                                    </div>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="is_covid" id="0" checked={formData.is_covid === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.no")}</p>
                                    </div>
                                </div>
                                {errors.is_covid && <span className="error">{errors.is_covid}</span>}
                                <div className="form-item">
                                    <label>{t("health_declaration.is_diseases")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="is_diseases" id="1" checked={formData.is_diseases === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.yes")}</p>
                                    </div>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="is_diseases" id="0" checked={formData.is_diseases === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.no")}</p>
                                    </div>
                                </div>
                                {errors.is_diseases && <span className="error">{errors.is_diseases}</span>}

                                <div className="form-item">
                                    <label className="form-label">{t("health_declaration.is_infectious")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                    <textarea type="text" disabled={!isEditing} className="form-control" id="is_infectious" value={formData.is_infectious || ""} onChange={handleInputChange} />
                                </div>
                                {errors.is_infectious && <span className="error">{errors.is_infectious}</span>}

                                <div className="form-item">
                                    <label>{t("health_declaration.close_contact")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="close_contact" id="1" checked={formData.close_contact === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.yes")}</p>
                                    </div>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="close_contact" id="0" checked={formData.close_contact === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("general.no")}</p>
                                    </div>
                                </div>
                                {errors.close_contact && <span className="error">{errors.close_contact}</span>}
                                <div className="form-item">
                                    <label>{t("health_declaration.no_refund")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="no_refund" id="1" checked={formData.no_refund === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.agree")}</p>
                                    </div>
                                    {/* <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="no_refund" id="0" checked={formData.no_refund === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.disagree")}</p>
                                    </div> */}
                                </div>
                                {errors.no_refund && <span className="error">{errors.no_refund}</span>}
                                <div className="form-item">
                                    <label>{t("health_declaration.cost_incurred")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="cost_incurred" id="1" checked={formData.cost_incurred === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.agree")}</p>
                                    </div>
                                    {/* <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="cost_incurred" id="0" checked={formData.cost_incurred === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.disagree")}</p>
                                    </div> */}
                                </div>
                                {errors.cost_incurred && <span className="error">{errors.cost_incurred}</span>}
                                <div className="form-item">
                                    <label>{t("health_declaration.disturb_ajahn")}
                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="disturb_ajahn" id="1" checked={formData.disturb_ajahn === "1"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.agree")}</p>
                                    </div>
                                    {/* <div className='radio'>
                                        <input className="form-check-input" disabled={!isEditing} type="radio" name="disturb_ajahn" id="0" checked={formData.disturb_ajahn === "0"} onChange={handleInputChange} required />
                                        <p className='radioLabel mb-0'>{t("health_declaration.disagree")}</p>
                                    </div> */}
                                </div>
                                {errors.disturb_ajahn && <span className="error">{errors.disturb_ajahn}</span>}

                                {isEditing &&
                                    <div className='submit-button d-flex justify-content-center mt-4 gap-3'>
                                        <Button buttonStyle='btn--primary' disabled={isSubmitting} onClick={handleSubmit} type="button">
                                            {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsave")}
                                        </Button>
                                    </div>
                                }
                            </form>
                        </div>
                    }
                </>
            }

        </>
    )
}

export default CreateHealthDeclaration